/**
 * Store for anything related to post information, searches, blacklists, post tags etc.
 */

import { TagCollection, ShortTag, Tag } from '../schemas/Tag';
import { Post } from '../schemas/Post';

import * as localForage from 'localforage';

localForage.config({
    driver: localForage.INDEXEDDB,
    name: 'msg',
    version: 1.0,
});

import { autorunAsync } from 'mobx';

import * as _ from 'lodash';

import { SEARCH_POSTS } from './queries/PostQueries';
import { observable } from 'mobx';
import client from '../client';
import { BlacklistPayload } from '../schemas/Blacklist';
import { BlacklistNode, BlacklistDirective } from '../components/Inputs/Blacklist/Blacklist';
import { parseNodes } from './nodeParser';

interface SearchOptions {
    tags: string[];
    limit: number;
    page?: number;
    sfw?: boolean;
    blacklist?: BlacklistPayload;
}

const CACHE_NAME = 'api_tag_store_location';

const BLACKLIST_CACHE = 'blacklist_tag_store_location';
const BLACKLIST_TRACERS = '';

// type Categories =       'general' | 'artist' | 'copyright' | 'species' | 'character';

const emptyCollection: TagCollection<ShortTag> = {
    general: {},
    artist: {},
    copyright: {},
    character: {},
    species: {},
};

interface SearchQuery {
    chips: string[];
    currentInput: string;
    sfw: boolean;
}

const DEFAULT_LIMIT = 45;

export class PostStore {
    // dont observe this, wayyyy to many things to watch
    // seriously dont
    tags: TagCollection<ShortTag> = {
        ...emptyCollection,
    };

    // less expensive to observe.
    @observable flatTags: string[] = [];

    @observable
    searchQuery: SearchQuery = {
        chips: [],
        currentInput: '',
        sfw: false,
    };

    /**
     * Describes a syntax for a blacklist.
     * each row holds a set of nodes, that hold values.
     * 
     * A node describes what action should be invoked and 
     * the payload for the action.
     * 
     * to block multiple tags, equiv to 
     * 
     * `a b c`
     * 
     * block 'a' -> and 'b' -> and 'c'
     * 
     * to block a tag unless a tag exists, equiv to
     * 
     * `-a -b c`
     * 
     * unless 'a' -> and 'b' -> block 'c'
     * 
     * @type {BlacklistNode[][]}
     * @memberof PostStore
     */
    @observable
    nodes: BlacklistNode[][] = [
        [
            observable({
                id: _.uniqueId(),
                value: '',
                directive: 0,
                choices: [BlacklistDirective.BLOCK, BlacklistDirective.UNLESS],
            }),
        ],
    ];

    /**
     * A terrible but effective way of granting a unique ID for each blacklist node for the react keys
     * 
     * @memberof PostStore
     */
    @observable
    tracers = [_.uniqueId()];

    @observable.shallow posts: Post<TagCollection<ShortTag>>[] = [];
    /**
     * Prevents duplicates, check if the post exists before pushing.
     * 
     * @type {{
     *         [key: string]: boolean;
     *     }}
     * @memberof PostStore
     */
    postExists: {
        [key: string]: boolean;
    } = {};
    @observable hasMore = false;

    // todo
    @observable activePosts: Post<TagCollection<Tag>>[] = [];

    /**
     * Kinda hacky, observable instance for the searchbox, sending tags from the tag-list directly into the searchbox.
     * 
     * @type {{
     *         add: string[],
     *         override: string,
     *     }}
     * @memberof PostStore
     */
    @observable sendChips: {
        add: string[],
        override: string,
    } = {
        add: [],
        override: '',
    };

    /**
     * (Self explanatory.)
     * 
     * @memberof PostStore
     */
    @observable searching = false;
    @observable lastSearchEmpty = false;

    addBlacklistNode(name: string) {
        const lastNode = _.last(this.nodes);
        if (lastNode![0].value === '') {
            lastNode![0].value = name;
        } else {
            this.nodes.push([observable({
                id: _.uniqueId(),
                value: name,
                directive: 0,
                choices: [BlacklistDirective.BLOCK, BlacklistDirective.UNLESS],
            })]);
        }
    }

    createFlatTags() {
        const tags = [];
        for (const category of Object.keys(this.tags)) {
            tags.push(...Object.keys(this.tags[category]));
        }

        this.flatTags = tags;
    }

    constructor() {
        autorunAsync(async () => {
            localStorage.setItem(BLACKLIST_TRACERS, JSON.stringify(this.tracers));
            if (this.nodes[0][0].value !== '') {
                localStorage.setItem(BLACKLIST_CACHE, JSON.stringify(this.nodes)); // auto save blacklist
            }
        });

        try {
            const blacklist = localStorage.getItem(BLACKLIST_CACHE);
            const tracers = localStorage.getItem(BLACKLIST_TRACERS);
            // load blacklist and save it to the active node #autosaves
            if (blacklist != null && tracers != null) {
                this.nodes = JSON.parse(blacklist);
                this.tracers = JSON.parse(tracers);
            }

            this.getTags().then(() => this.createFlatTags());
        } catch (up) {
            throw up;
        }
    }

    initiateSearch = async (page: number, freshPosts?: boolean) => {
        if (page < 0) {
            return;
        }

        const { currentInput, chips } = this.searchQuery;

        const dangling = currentInput && _.last(chips) !== currentInput ? currentInput : '';

        const blacklist = parseNodes(this.nodes);
                                // save the mobile users
        const tags = [...chips, ...dangling.split(' ')].filter(is => is);

        this.search(
            {
                page,
                blacklist,
                tags,
                sfw: this.searchQuery!.sfw,
                // sfw: this.searchQuery.sfw,
                limit: DEFAULT_LIMIT,
            },
            freshPosts,
        );
    }

    search = async (options: SearchOptions, freshPosts?: boolean) => {
        this.searching = true;
        this.lastSearchEmpty = false;
        this.hasMore = false; // dont remove unless you like infinite loops

        if (freshPosts) {
            this.postExists = {};
        }

        try {
            if (freshPosts) {
                this.posts = [];
            }

            const result = await client.query({
                variables: options,
                query: SEARCH_POSTS,
            });

            const _posts = (result.data as {
                search: Post<{ [key: string]: (ShortTag | undefined)[] }>[];
            }).search;
            if (_posts.length <= 0) {
                this.hasMore = false;
                this.searching = false;
                this.lastSearchEmpty = true;
                return;
            }

            const posts = _posts
                .map(p => {
                    const post = {
                        ...p,
                        tags: {
                            ...emptyCollection,
                        },
                    };

                    for (const category of Object.keys(p.tags)) {
                        const bundle = {};

                        for (const tag of p.tags[category]) {
                            if (typeof tag !== 'object') {
                                continue;
                            }

                            this.tags[category][tag.name] = tag;
                            bundle[tag.name] = tag;
                        }

                        post.tags[category] = bundle;
                    }

                    return post;
                })
                .filter(v => v.file_ext !== 'swf');

            this.hasMore = true;

            if (freshPosts) {
                this.posts = posts;

                for (const post of posts) {
                    this.postExists[post.id] = true;
                }
            } else {
                const deduped = posts.filter(p => {
                    if (this.postExists[p.id]) {
                        return false;
                    }

                    this.postExists[p.id] = true;
                    return true;
                });

                this.posts = [...this.posts, ...deduped];
            }
            this.searching = false;
            this.createFlatTags();
            this.saveTags();
        } catch (e) {
            this.searching = false;
            throw e; // TODO: change this to be a notification error instead
        }
    }

    saveTags = async () => {
        await localForage.setItem(CACHE_NAME, this.tags);
    }

    getTags = async () => {
        const result = await localForage.getItem<TagCollection<ShortTag>>(CACHE_NAME);
        if (result) {
            for (const [key, category] of Object.entries(result)) {
                this.tags[key] = category;
            }
        }
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/stores/PostStore.ts